// Recipe Module Phase 2 (docs/RECIPE_MODULE.md) — create/edit a baker's-percentage dough recipe.
// Flour is the fixed 100% reference (standard baker's percentage convention — the OPPOSITE of
// PercentageRecipeEditorPage, where every line sums to ~100% of the final batch); hydration/salt/
// yeast/oil are each a % of total flour, slider-adjustable with a live preview at a 1000g-flour
// reference scale. Pre-ferments (biga/poolish/levain/sourdough/sponge) all reduce to the same
// flour-%/hydration-%/leavening-% shape — only the typical defaults differ per type. Autolyse is
// the one exception: a plain flour+water rest with no leavening agent at all (see
// PRE_FERMENT_CARRIES_YEAST in config/doughLabels.js).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import recipeService from '../services/recipeService';
import ingredientService from '../services/ingredientService';
import SearchableSelect from '../components/SearchableSelect';
import GenerateRecipeModal from '../components/GenerateRecipeModal';
import { YEAST_TYPE_OPTIONS, PRE_FERMENT_DEFAULTS, PRE_FERMENT_LABELS, PRE_FERMENT_CARRIES_YEAST, PRE_FERMENT_DESCRIPTIONS } from '../config/doughLabels';

function SliderRow({ label, value, onChange, min = 0, max = 100, step = 0.5, suffix = '%' }) {
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="form-label small fw-semibold mb-0">{label}</label>
        <div className="input-group input-group-sm" style={{ width: 100 }}>
          <input type="number" step="0.01" className="form-control text-end" value={value}
            onChange={e => onChange(e.target.value)} />
          <span className="input-group-text">{suffix}</span>
        </div>
      </div>
      <input type="range" className="form-range" min={min} max={max} step={step}
        value={value === '' || isNaN(parseFloat(value)) ? min : value}
        onChange={e => onChange(e.target.value)} />
    </div>
  );
}

export default function DoughRecipeEditorPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const location = useLocation();

  const [ingredients, setIngredients] = useState([]);
  const [priceMap, setPriceMap] = useState({});

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');

  const [hydrationPercent, setHydrationPercent] = useState('65');
  const [saltPercent, setSaltPercent] = useState('2.5');
  const [saltIngredientId, setSaltIngredientId] = useState('');
  const [extraIngredients, setExtraIngredients] = useState([]);
  const [yeastType, setYeastType] = useState('fresh');
  const [yeastPercent, setYeastPercent] = useState('2');
  const [yeastIngredientId, setYeastIngredientId] = useState('');

  const [preFermentType, setPreFermentType] = useState('none');
  const [preFermentFlourPercent, setPreFermentFlourPercent] = useState('');
  const [preFermentHydrationPercent, setPreFermentHydrationPercent] = useState('');
  const [preFermentFermentationHours, setPreFermentFermentationHours] = useState('');
  const [preFermentFermentationTempC, setPreFermentFermentationTempC] = useState('');

  const [defaultBallWeightG, setDefaultBallWeightG] = useState('250');
  const [outputIngredientId, setOutputIngredientId] = useState('');
  const [flourLines, setFlourLines] = useState([{ ingredientId: '', percentOfFlour: '100' }]);
  const [steps, setSteps] = useState([{ instruction: '' }]);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    ingredientService.getIngredients().then(setIngredients).catch(() => setIngredients([]));
    ingredientService.getCurrentPrices()
      .then(rows => {
        const map = {};
        for (const r of rows) map[String(r.ingredientId)] = Number(r.pricePerKg);
        setPriceMap(map);
      })
      .catch(() => {});
  }, []);

  const loadRecipe = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError('');
    try {
      const data = await recipeService.getDoughRecipe(id);
      setName(data.name || '');
      setCategory(data.category || '');
      setNotes(data.notes || '');
      setStatus(data.status || 'draft');
      setHydrationPercent(String(data.hydrationPercent));
      setSaltPercent(String(data.saltPercent));
      setSaltIngredientId(data.saltIngredientId ? String(data.saltIngredientId) : '');
      setExtraIngredients((data.extraIngredients || []).map(e => ({
        label: e.label || '',
        ingredientId: e.ingredientId ? String(e.ingredientId) : '',
        percentOfFlour: String(e.percentOfFlour),
        phase: e.phase === 'preferment' ? 'preferment' : 'final',
      })));
      setYeastType(data.yeastType || 'fresh');
      setYeastPercent(String(data.yeastPercent));
      setYeastIngredientId(data.yeastIngredientId ? String(data.yeastIngredientId) : '');
      setPreFermentType(data.preFermentType || 'none');
      setPreFermentFlourPercent(data.preFermentFlourPercent != null ? String(data.preFermentFlourPercent) : '');
      setPreFermentHydrationPercent(data.preFermentHydrationPercent != null ? String(data.preFermentHydrationPercent) : '');
      setPreFermentFermentationHours(data.preFermentFermentationHours != null ? String(data.preFermentFermentationHours) : '');
      setPreFermentFermentationTempC(data.preFermentFermentationTempC != null ? String(data.preFermentFermentationTempC) : '');
      setDefaultBallWeightG(data.defaultBallWeightG != null ? String(data.defaultBallWeightG) : '250');
      setOutputIngredientId(data.outputIngredientId ? String(data.outputIngredientId) : '');
      setFlourLines((data.flourLines || []).map(l => ({ ingredientId: String(l.ingredientId), percentOfFlour: String(l.percentOfFlour) })));
      setSteps((data.steps || []).length > 0 ? data.steps.map(s => ({ instruction: s.instruction })) : [{ instruction: '' }]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recipe.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { loadRecipe(); }, [loadRecipe]);

  const ingredientOptions = useMemo(() => ingredients.map(i => ({
    value: String(i.id),
    label: i.rawMaterialCode ? `${i.name} (${i.rawMaterialCode})` : i.name,
  })), [ingredients]);

  // Normalize+substring fuzzy match against the real catalog — the AI returns plain ingredient/
  // flour names (it doesn't know our ids), same fallback-matching convention already used
  // elsewhere in this app (receipt scan, PO scan) when an AI guess needs reconciling against real data.
  const fuzzyMatchIngredient = (name) => {
    if (!name) return null;
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = norm(name);
    const exact = ingredients.find(i => norm(i.name) === target);
    if (exact) return exact;
    return ingredients.find(i => norm(i.name).includes(target) || target.includes(norm(i.name))) || null;
  };

  const applyDraft = (draft) => {
    setName(draft.name || '');
    setCategory(draft.category || '');
    setHydrationPercent(String(draft.hydrationPercent ?? '65'));
    setSaltPercent(String(draft.saltPercent ?? '2'));
    setYeastType(draft.yeastType || 'fresh');
    setYeastPercent(String(draft.yeastPercent ?? '1'));
    setPreFermentType(draft.preFermentType || 'none');
    setPreFermentFlourPercent(draft.preFermentFlourPercent != null ? String(draft.preFermentFlourPercent) : '');
    setPreFermentHydrationPercent(draft.preFermentHydrationPercent != null ? String(draft.preFermentHydrationPercent) : '');
    setPreFermentFermentationHours(draft.preFermentFermentationHours != null ? String(draft.preFermentFermentationHours) : '');
    setPreFermentFermentationTempC(draft.preFermentFermentationTempC != null ? String(draft.preFermentFermentationTempC) : '');
    setFlourLines((draft.flourLines || []).map(l => {
      const match = fuzzyMatchIngredient(l.flourName);
      return { ingredientId: match ? String(match.id) : '', percentOfFlour: String(l.percentOfFlour ?? '') };
    }));
    setExtraIngredients((draft.extraIngredients || []).map(e => {
      const match = fuzzyMatchIngredient(e.label);
      return {
        label: e.label || '',
        ingredientId: match ? String(match.id) : '',
        percentOfFlour: String(e.percentOfFlour ?? ''),
        phase: e.phase === 'preferment' ? 'preferment' : 'final',
      };
    }));
    setSteps((draft.steps || []).map(s => ({ instruction: s })));
    setShowGenerateModal(false);
  };

  // A draft handed off from the Knowledge Base chat's "➕ Add to your recipes" button arrives via
  // router state rather than the in-page modal — apply it once the real ingredient catalog is
  // loaded (so fuzzy-matching has something to match against), then clear the state so it doesn't
  // re-apply on a back-navigation or refresh.
  useEffect(() => {
    if (isNew && ingredients.length > 0 && location.state?.generatedDraft) {
      applyDraft(location.state.generatedDraft);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, ingredients, location.state]);

  const addFlourLine = () => setFlourLines(prev => [...prev, { ingredientId: '', percentOfFlour: '' }]);
  const removeFlourLine = (idx) => setFlourLines(prev => prev.filter((_, i) => i !== idx));
  const setFlourLineField = (idx, field, value) => setFlourLines(prev => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));

  const addExtraIngredient = () => setExtraIngredients(prev => [...prev, { label: '', ingredientId: '', percentOfFlour: '', phase: 'final' }]);
  const removeExtraIngredient = (idx) => setExtraIngredients(prev => prev.filter((_, i) => i !== idx));
  const setExtraIngredientField = (idx, field, value) => setExtraIngredients(prev => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));

  const addStep = () => setSteps(prev => [...prev, { instruction: '' }]);
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));
  const setStepField = (idx, value) => setSteps(prev => prev.map((s, i) => (i === idx ? { instruction: value } : s)));

  const handlePreFermentTypeChange = (newType) => {
    setPreFermentType(newType);
    if (newType !== 'none' && !preFermentFlourPercent && PRE_FERMENT_DEFAULTS[newType]) {
      setPreFermentFlourPercent(String(PRE_FERMENT_DEFAULTS[newType].flourPercent));
      setPreFermentHydrationPercent(String(PRE_FERMENT_DEFAULTS[newType].hydrationPercent));
    }
  };

  const flourTotalPercent = useMemo(() => flourLines.reduce((s, l) => s + (parseFloat(l.percentOfFlour) || 0), 0), [flourLines]);

  // Live preview at a reference 1000g of flour — mirrors the backend's computeDoughBreakdown()
  // exactly (routes/recipeRoutes.js), just computed client-side for instant slider feedback.
  const preview = useMemo(() => {
    const hydration = parseFloat(hydrationPercent) || 0;
    const salt = parseFloat(saltPercent) || 0;
    const yeast = parseFloat(yeastPercent) || 0;
    const extraPercent = extraIngredients.reduce((s, e) => s + (parseFloat(e.percentOfFlour) || 0), 0);
    const totalPercent = 100 + hydration + salt + yeast + extraPercent;
    const flourG = 1000;
    const doughG = flourG * totalPercent / 100;
    const waterG = flourG * hydration / 100;
    const saltG = flourG * salt / 100;
    const yeastG = flourG * yeast / 100;
    const extraBreakdown = extraIngredients.map(e => ({
      label: e.label || (ingredients.find(i => String(i.id) === e.ingredientId)?.name) || 'Ingredient',
      phase: e.phase === 'preferment' ? 'preferment' : 'final',
      quantityG: flourG * (parseFloat(e.percentOfFlour) || 0) / 100,
    }));

    let cost = 0;
    let unpriced = 0;
    let anyPriced = false;
    for (const l of flourLines) {
      const pct = parseFloat(l.percentOfFlour);
      if (!l.ingredientId || !pct) continue;
      const price = priceMap[l.ingredientId];
      if (price == null) { unpriced++; continue; }
      anyPriced = true;
      cost += (flourG * pct / 100 / 1000) * price;
    }
    if (saltIngredientId) {
      const price = priceMap[saltIngredientId];
      if (price == null) unpriced++; else { anyPriced = true; cost += (saltG / 1000) * price; }
    }
    if (yeastIngredientId) {
      const price = priceMap[yeastIngredientId];
      if (price == null) unpriced++; else { anyPriced = true; cost += (yeastG / 1000) * price; }
    }
    extraIngredients.forEach((e, idx) => {
      if (!e.ingredientId) return; // no priced ingredient linked — cost unknown, not an error
      const price = priceMap[e.ingredientId];
      if (price == null) unpriced++; else { anyPriced = true; cost += (extraBreakdown[idx].quantityG / 1000) * price; }
    });

    let preFerment = null;
    let finalFlourG = flourG, finalWaterG = waterG, finalYeastG = 0;
    let finalExtraBreakdown = extraBreakdown;
    const pfFlourPct = parseFloat(preFermentFlourPercent);
    const pfHydrationPct = parseFloat(preFermentHydrationPercent);
    if (preFermentType !== 'none' && pfFlourPct > 0) {
      const pfFlourG = flourG * pfFlourPct / 100;
      const pfWaterG = pfFlourG * (pfHydrationPct || 0) / 100;
      const carriesYeast = PRE_FERMENT_CARRIES_YEAST[preFermentType] !== false;
      preFerment = { flourG: pfFlourG, waterG: pfWaterG, yeastG: carriesYeast ? yeastG : 0, extraIngredients: extraBreakdown.filter(e => e.phase === 'preferment') };
      finalFlourG = flourG - pfFlourG;
      finalWaterG = waterG - pfWaterG;
      finalYeastG = carriesYeast ? 0 : yeastG;
      finalExtraBreakdown = extraBreakdown.filter(e => e.phase !== 'preferment');
    }

    const costPerKgFlour = unpriced > 0 || !anyPriced ? null : Number(cost.toFixed(4));
    const costPerKgDough = costPerKgFlour != null ? Number((costPerKgFlour / (doughG / 1000)).toFixed(4)) : null;
    return { totalPercent, flourG, doughG, waterG, saltG, yeastG, extraBreakdown, finalExtraBreakdown, preFerment, finalFlourG, finalWaterG, finalYeastG, costPerKgFlour, costPerKgDough, unpriced };
  }, [hydrationPercent, saltPercent, yeastPercent, extraIngredients, ingredients, flourLines, priceMap, saltIngredientId, yeastIngredientId, preFermentType, preFermentFlourPercent, preFermentHydrationPercent]);

  const handleSave = async () => {
    setError('');
    if (!name.trim()) return setError('Name is required.');
    if (!hydrationPercent || parseFloat(hydrationPercent) <= 0) return setError('A valid hydration % is required.');
    if (!saltPercent || parseFloat(saltPercent) <= 0) return setError('A valid salt % is required.');
    if (yeastPercent === '' || isNaN(parseFloat(yeastPercent)) || parseFloat(yeastPercent) < 0) return setError('A valid yeast % is required.');
    const validFlourLines = flourLines.filter(l => l.ingredientId && l.percentOfFlour !== '');
    if (validFlourLines.length === 0) return setError('At least one flour is required.');
    const validExtraIngredients = extraIngredients.filter(e => e.percentOfFlour !== '' || e.label.trim() || e.ingredientId);
    for (const e of validExtraIngredients) {
      if (!e.percentOfFlour || parseFloat(e.percentOfFlour) <= 0) return setError('Every extra ingredient needs a positive percentage.');
      if (!e.ingredientId && !e.label.trim()) return setError('Every extra ingredient needs either a linked ingredient or a label (e.g. "Sugar").');
    }
    if (preFermentType !== 'none' && (!preFermentFlourPercent || !preFermentHydrationPercent)) {
      return setError('A pre-ferment flour % and hydration % are both required when a pre-ferment type is selected.');
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category: category.trim() || null,
        notes: notes.trim() || null,
        hydrationPercent: Number(hydrationPercent),
        saltPercent: Number(saltPercent),
        saltIngredientId: saltIngredientId ? Number(saltIngredientId) : null,
        extraIngredients: validExtraIngredients.map(e => ({
          label: e.label.trim() || null,
          ingredientId: e.ingredientId ? Number(e.ingredientId) : null,
          percentOfFlour: Number(e.percentOfFlour),
          phase: preFermentType !== 'none' && e.phase === 'preferment' ? 'preferment' : 'final',
        })),
        yeastType,
        yeastPercent: Number(yeastPercent),
        yeastIngredientId: yeastIngredientId ? Number(yeastIngredientId) : null,
        preFermentType,
        preFermentFlourPercent: preFermentType !== 'none' ? Number(preFermentFlourPercent) : null,
        preFermentHydrationPercent: preFermentType !== 'none' ? Number(preFermentHydrationPercent) : null,
        preFermentFermentationHours: preFermentFermentationHours !== '' ? Number(preFermentFermentationHours) : null,
        preFermentFermentationTempC: preFermentFermentationTempC !== '' ? Number(preFermentFermentationTempC) : null,
        defaultBallWeightG: defaultBallWeightG !== '' ? Number(defaultBallWeightG) : null,
        outputIngredientId: outputIngredientId ? Number(outputIngredientId) : null,
        flourLines: validFlourLines.map(l => ({ ingredientId: Number(l.ingredientId), percentOfFlour: Number(l.percentOfFlour) })),
        steps: steps.filter(s => s.instruction.trim()).map(s => ({ instruction: s.instruction.trim() })),
      };
      if (isNew) {
        const created = await recipeService.createDoughRecipe(payload);
        navigate(`/recipes/dough/${created.id}/edit`);
      } else {
        await recipeService.updateDoughRecipe(id, payload);
        await loadRecipe();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save recipe.');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await recipeService.approveDoughRecipe(id);
      await loadRecipe();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve recipe.');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center text-muted py-5">
        <div className="spinner-border spinner-border-sm me-2" />Loading…
      </div>
    );
  }

  const CARD_STYLE = (color) => ({ border: '1px solid #e9ecef', borderTopWidth: 3, borderTopStyle: 'solid', borderTopColor: color });
  const SECTION_HEADER_STYLE = { fontSize: 11, letterSpacing: 1, fontWeight: 700 };

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center mb-4 pb-3 gap-2" style={{ borderBottom: '2px solid #f1f3f5' }}>
        <div>
          <h4 className="mb-1">{isNew ? '🍞 New Dough Recipe' : `🍞 ${name}`}</h4>
          {!isNew && (
            <span className={`badge rounded-pill ${status === 'approved' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: 12, padding: '5px 12px' }}>
              {status === 'approved' ? '✓ Approved' : 'Draft'}
            </span>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {isNew && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowGenerateModal(true)}>✨ Generate with AI</button>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/recipes')}>← Back to Library</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {showGenerateModal && (
        <GenerateRecipeModal recipeType="dough" onApply={applyDraft} onClose={() => setShowGenerateModal(false)} />
      )}

      <div className="card mb-3" style={CARD_STYLE('#0d6efd')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>🏷️ Recipe Details</div>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Name <span className="text-danger">*</span></label>
              <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Altamura-Style Bread" />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Category</label>
              <input className="form-control" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Bread" />
            </div>
          </div>
          <div className="mb-1">
            <label className="form-label small fw-semibold">Notes</label>
            <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#198754')}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="text-uppercase text-muted" style={SECTION_HEADER_STYLE}>🌾 Flour Blend</div>
            <div className="text-end">
              <div className="small" style={{ color: Math.abs(flourTotalPercent - 100) > 1 ? '#fd7e14' : '#6c757d' }}>
                Blend total: {flourTotalPercent.toFixed(2)}%{Math.abs(flourTotalPercent - 100) > 1 && ' ⚠ should be 100%'}
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '55%' }}>Flour</th>
                  <th style={{ width: 120 }}>% of flour</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {flourLines.map((l, idx) => (
                  <tr key={idx}>
                    <td className="align-top pt-2">
                      <SearchableSelect
                        options={ingredientOptions}
                        value={l.ingredientId}
                        onChange={v => setFlourLineField(idx, 'ingredientId', v)}
                        placeholder="Select flour…"
                        size="sm"
                      />
                    </td>
                    <td className="align-top pt-2">
                      <div className="input-group input-group-sm">
                        <input type="number" step="0.01" className="form-control" value={l.percentOfFlour}
                          onChange={e => setFlourLineField(idx, 'percentOfFlour', e.target.value)} />
                        <span className="input-group-text">%</span>
                      </div>
                    </td>
                    <td className="align-top pt-2">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeFlourLine(idx)} disabled={flourLines.length <= 1}>
                        &times;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-sm btn-outline-primary" onClick={addFlourLine}>+ Add Flour</button>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#6f42c1')}>
        <div className="card-body">
          <div className="row">
            <div className="col-md-7">
              <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>⚗️ Formula (% of flour weight)</div>
              <SliderRow label="Hydration" value={hydrationPercent} onChange={setHydrationPercent} min={40} max={120} />
              <div className="row g-2 align-items-start">
                <div className="col-12 col-md-8"><SliderRow label="Salt" value={saltPercent} onChange={setSaltPercent} min={0} max={5} step={0.1} /></div>
                <div className="col-12 col-md-4">
                  <label className="form-label small mb-1">Ingredient</label>
                  <SearchableSelect options={ingredientOptions} value={saltIngredientId} onChange={setSaltIngredientId} placeholder="optional" size="sm" />
                </div>
              </div>

              <div className="row g-2 align-items-start">
                <div className="col-12 col-md-6">
                  <SliderRow label="Yeast" value={yeastPercent} onChange={setYeastPercent} min={0} max={10} step={0.05} />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label small mb-1">Type</label>
                  <select className="form-select form-select-sm" value={yeastType} onChange={e => setYeastType(e.target.value)}>
                    {YEAST_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label small mb-1">Ingredient</label>
                  <SearchableSelect options={ingredientOptions} value={yeastIngredientId} onChange={setYeastIngredientId} placeholder="optional" size="sm" />
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label small fw-semibold mb-0">Extra ingredients (sugar, malt, butter, oil, milk, eggs…)</label>
              </div>
              {extraIngredients.map((e, idx) => (
                <div key={idx} className="border rounded p-2 mb-2">
                  <div className="row g-2 mb-2">
                    <div className="col-12 col-md-6">
                      <input className="form-control form-control-sm" placeholder="Label (e.g. Sugar)"
                        value={e.label} onChange={ev => setExtraIngredientField(idx, 'label', ev.target.value)} />
                    </div>
                    <div className="col-12 col-md-6">
                      <SearchableSelect options={ingredientOptions} value={e.ingredientId}
                        onChange={v => setExtraIngredientField(idx, 'ingredientId', v)} placeholder="Ingredient (for cost, optional)" size="sm" />
                    </div>
                  </div>
                  <div className="row g-2 align-items-center">
                    <div className="col-6 col-md-3">
                      <div className="input-group input-group-sm">
                        <input type="number" step="0.01" className="form-control" value={e.percentOfFlour}
                          onChange={ev => setExtraIngredientField(idx, 'percentOfFlour', ev.target.value)} />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                    {preFermentType !== 'none' && (
                      <div className="col-6 col-md-5">
                        <select className="form-select form-select-sm" value={e.phase || 'final'}
                          onChange={ev => setExtraIngredientField(idx, 'phase', ev.target.value)}>
                          <option value="final">Final dough</option>
                          <option value="preferment">{PRE_FERMENT_LABELS[preFermentType]}</option>
                        </select>
                      </div>
                    )}
                    <div className="col-auto ms-auto">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeExtraIngredient(idx)}>&times; Remove</button>
                    </div>
                  </div>
                </div>
              ))}
              <button className="btn btn-sm btn-outline-primary" onClick={addExtraIngredient}>+ Add Extra Ingredient</button>
            </div>
            <div className="col-md-5">
              <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>🧮 Preview (per 1kg flour)</div>
              <div className="card bg-light border-0 p-3">
                <div className="d-flex justify-content-between"><span className="text-muted">Flour</span><strong>{preview.flourG.toFixed(0)} g</strong></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Water</span><strong>{preview.waterG.toFixed(0)} g</strong></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Salt</span><strong>{preview.saltG.toFixed(1)} g</strong></div>
                <div className="d-flex justify-content-between"><span className="text-muted">{YEAST_TYPE_OPTIONS.find(o => o.value === yeastType)?.label || 'Yeast'}</span><strong>{preview.yeastG.toFixed(2)} g</strong></div>
                {preview.extraBreakdown.map((e, idx) => (
                  <div key={idx} className="d-flex justify-content-between">
                    <span className="text-muted">{e.label}{preFermentType !== 'none' && e.phase === 'preferment' ? ` (${PRE_FERMENT_LABELS[preFermentType]})` : ''}</span>
                    <strong>{e.quantityG.toFixed(1)} g</strong>
                  </div>
                ))}
                <hr className="my-2" />
                <div className="d-flex justify-content-between"><span className="text-muted">Total dough</span><strong>{preview.doughG.toFixed(0)} g</strong></div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Cost</span>
                  <strong style={{ color: '#198754' }}>
                    {preview.costPerKgDough == null ? '—' : `$${preview.costPerKgDough.toFixed(4)}/kg dough`}
                  </strong>
                </div>
                {preview.unpriced > 0 && <div className="text-warning" style={{ fontSize: 11 }}>{preview.unpriced} ingredient(s) unpriced</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#fd7e14')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>🫙 Pre-ferment (optional)</div>
          <div className="row g-3 mb-3">
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Type</label>
              <select className="form-select form-select-sm" value={preFermentType} onChange={e => handlePreFermentTypeChange(e.target.value)}>
                {Object.entries(PRE_FERMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="alert alert-light border small mb-3 py-2 px-3" style={{ fontSize: 12 }}>
            <strong>{PRE_FERMENT_LABELS[preFermentType]}:</strong> {PRE_FERMENT_DESCRIPTIONS[preFermentType]}
          </div>
          <div className="row g-3">
            {preFermentType !== 'none' && (
              <>
                <div className="col-6 col-md-3">
                  <label className="form-label small fw-semibold">% of total flour</label>
                  <div className="input-group input-group-sm">
                    <input type="number" step="0.01" className="form-control" value={preFermentFlourPercent}
                      onChange={e => setPreFermentFlourPercent(e.target.value)} />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small fw-semibold">Its own hydration</label>
                  <div className="input-group input-group-sm">
                    <input type="number" step="0.01" className="form-control" value={preFermentHydrationPercent}
                      onChange={e => setPreFermentHydrationPercent(e.target.value)} />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small fw-semibold">Hours</label>
                  <input type="number" step="0.5" className="form-control form-control-sm" value={preFermentFermentationHours}
                    onChange={e => setPreFermentFermentationHours(e.target.value)} placeholder="e.g. 16" />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small fw-semibold">Temp °C</label>
                  <input type="number" step="0.5" className="form-control form-control-sm" value={preFermentFermentationTempC}
                    onChange={e => setPreFermentFermentationTempC(e.target.value)} placeholder="e.g. 18" />
                </div>
              </>
            )}
          </div>
          {preview.preFerment && (
            <div className="text-muted mt-2" style={{ fontSize: 12 }}>
              At 1kg flour: {PRE_FERMENT_LABELS[preFermentType]} uses {preview.preFerment.flourG.toFixed(0)}g flour +
              {' '}{preview.preFerment.waterG.toFixed(0)}g water
              {preFermentType !== 'autolyse' && <> + all {preview.preFerment.yeastG.toFixed(2)}g yeast</>}
              {preview.preFerment.extraIngredients.length > 0 && (
                <> + {preview.preFerment.extraIngredients.map(e => `${e.quantityG.toFixed(1)}g ${e.label}`).join(' + ')}</>
              )} —
              {' '}final dough mix adds the remaining {preview.finalFlourG.toFixed(0)}g flour + {preview.finalWaterG.toFixed(0)}g water
              {preFermentType === 'autolyse' && <> + all {preview.finalYeastG.toFixed(2)}g yeast</>}
              {' '}+ the salt
              {preview.finalExtraBreakdown.length > 0 && (
                <> + {preview.finalExtraBreakdown.map(e => e.label).join(' + ')}</>
              )}.
            </div>
          )}
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>⚙️ Run Defaults</div>
          <div className="col-md-4">
            <label className="form-label small fw-semibold">Default ball weight (g)</label>
            <input type="number" step="1" className="form-control form-control-sm" style={{ maxWidth: 160 }}
              value={defaultBallWeightG} onChange={e => setDefaultBallWeightG(e.target.value)} placeholder="e.g. 250" />
            <div className="form-text" style={{ fontSize: 11 }}>Prefilled when running this recipe by dough-ball count — editable each time.</div>
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#0dcaf0')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-1" style={SECTION_HEADER_STYLE}>🏷️ Traceability (optional)</div>
          <div className="text-muted mb-3" style={{ fontSize: 12 }}>
            Link this recipe's finished dough to a real ingredient and running it will auto-assign
            a lot code and credit the real output into Ingredient Inventory — traceable when later
            used in another recipe/product.
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Output Ingredient</label>
              <SearchableSelect
                options={ingredientOptions}
                value={outputIngredientId}
                onChange={setOutputIngredientId}
                placeholder="None — this run won't be credited to inventory"
                size="sm"
              />
              <div className="form-text" style={{ fontSize: 11 }}>Doesn't exist yet? Create it first in Ingredients Hub, then link it here.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#20c997')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-1" style={SECTION_HEADER_STYLE}>📋 Process Steps</div>
          <div className="text-muted mb-3" style={{ fontSize: 12 }}>
            Automatically translated to Spanish when saved, so staff with their account language set
            to Spanish can read and follow them too.
          </div>
          {steps.map((s, idx) => (
            <div key={idx} className="d-flex gap-2 mb-2 align-items-start">
              <span className="text-muted pt-2" style={{ width: 24 }}>{idx + 1}.</span>
              <textarea className="form-control form-control-sm" rows={1} value={s.instruction}
                onChange={e => setStepField(idx, e.target.value)} placeholder="e.g. Mix biga and let ferment 16 hours at 18°C" />
              <button className="btn btn-sm btn-outline-danger" onClick={() => removeStep(idx)} disabled={steps.length <= 1}>
                &times;
              </button>
            </div>
          ))}
          <button className="btn btn-sm btn-outline-primary" onClick={addStep}>+ Add Step</button>
        </div>
      </div>

      <div className="d-flex gap-2">
        <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? '✨ Create Recipe' : '💾 Save Changes'}
        </button>
        {!isNew && status === 'draft' && (
          <button className="btn btn-success px-4" onClick={handleApprove} disabled={approving}>
            {approving ? 'Approving…' : '✓ Approve Recipe'}
          </button>
        )}
      </div>
    </div>
  );
}
